<script setup lang="ts">
import { ref, inject, computed, watch, Ref } from 'vue'
import { toast } from 'vue-sonner'
import vhostService from '@/services/Vhost'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Search, Shield, Users, ChevronDown, ChevronRight, Plus, Save, Trash2 } from 'lucide-vue-next'
import { permissionCategories, allPermissionIds } from '@/types/Permissions'
import type { PermissionGroup } from '@/types/Permissions'

const vhostId = inject<Ref<string> | null>('vhostId', null)
const { getPermissionGroups: gqlGetPermGroups, updatePermissionGroup: gqlUpdatePermGroup, createPermissionGroup: gqlCreatePermGroup, deletePermissionGroup: gqlDeletePermGroup } = vhostService()
const searchQuery = ref('')
const activeTab = ref('groups')
const permissionGroups = ref<PermissionGroup[]>([])
const selectedGroup = ref<PermissionGroup | null>(null)
const expandedCategories = ref<Set<string>>(new Set())
const loading = ref(true)
const showCreateDialog = ref(false)
const newGroupName = ref('')
const newGroupDescription = ref('')

// Normalize permissions — ensure array format, lowercase for matching
const normalizePermissions = (groups: any[]): PermissionGroup[] => {
	return groups.map(g => {
		let perms: string[]
		if (Array.isArray(g.permissions)) {
			perms = g.permissions
		} else if (typeof g.permissions === 'string') {
			try { perms = JSON.parse(g.permissions) } catch { perms = [] }
		} else {
			perms = []
		}
		return {
			...g,
			permissions: perms.map((p: string) => String(p).trim().toLowerCase()),
		}
	})
}

// Reactive Set of the selected group's permissions — drives checkbox state
const activePermsSet = computed<Set<string>>(() => {
	if (!selectedGroup.value?.permissions) return new Set<string>()
	return new Set<string>(selectedGroup.value.permissions)
})

// Fetch permission groups from backend
const fetchPermissionGroups = async () => {
	if (!vhostId?.value) return
	loading.value = true
	try {
		const raw = await gqlGetPermGroups(Number(vhostId.value))
		permissionGroups.value = normalizePermissions(raw)
	} catch (error) {
		console.error('Error fetching permission groups:', error)
	} finally {
		loading.value = false
	}
}

// Toggle category expansion
const toggleCategory = (category: string) => {
	if (expandedCategories.value.has(category)) {
		expandedCategories.value.delete(category)
	} else {
		expandedCategories.value.add(category)
	}
}

// Check if a permission is enabled for the selected group (case-insensitive)
const hasPermission = (permId: string): boolean => {
	return activePermsSet.value.has(permId.trim().toLowerCase())
}

// Toggle permission for the selected group (always store lowercase)
const togglePermission = (permId: string) => {
	if (!selectedGroup.value) return
	if (!selectedGroup.value.permissions) {
		selectedGroup.value.permissions = []
	}
	const lc = permId.toLowerCase()
	const idx = selectedGroup.value.permissions.indexOf(lc)
	if (idx >= 0) {
		selectedGroup.value.permissions.splice(idx, 1)
	} else {
		selectedGroup.value.permissions.push(lc)
	}
}

// Save permission group changes
const savePermissionGroup = async () => {
	if (!selectedGroup.value || !vhostId?.value) return
	try {
		await gqlUpdatePermGroup(Number(vhostId.value), selectedGroup.value.id, selectedGroup.value.permissions)
		toast.success('Permission group saved successfully')
	} catch (error) {
		console.error('Error saving permission group:', error)
		toast.error('Failed to save permission group')
	}
}

// Create new permission group
const createPermissionGroup = async () => {
	if (!vhostId?.value || !newGroupName.value) return
	try {
		await gqlCreatePermGroup(Number(vhostId.value), newGroupName.value, [])
		toast.success('Permission group created')
		newGroupName.value = ''
		newGroupDescription.value = ''
		showCreateDialog.value = false
		await fetchPermissionGroups()
	} catch (error) {
		console.error('Error creating permission group:', error)
		toast.error('Failed to create permission group')
	}
}

// Delete a permission group
const deletePermissionGroup = async (groupId: number) => {
	if (!vhostId?.value) return
	try {
		await gqlDeletePermGroup(Number(vhostId.value), groupId)
		toast.success('Permission group deleted')
		if (selectedGroup.value?.id === groupId) {
			selectedGroup.value = null
		}
		await fetchPermissionGroups()
	} catch (error) {
		console.error('Error deleting permission group:', error)
		toast.error('Failed to delete permission group')
	}
}

// Filtered categories based on search
const filteredCategories = computed(() => {
	if (!searchQuery.value) return permissionCategories
	const query = searchQuery.value.toLowerCase()
	const result: Record<string, typeof permissionCategories[string]> = {}
	for (const [category, perms] of Object.entries(permissionCategories)) {
		const filtered = perms.filter(
			p => p.name.toLowerCase().includes(query) ||
				p.id.toLowerCase().includes(query) ||
				(p.description && p.description.toLowerCase().includes(query)) ||
				category.toLowerCase().includes(query)
		)
		if (filtered.length > 0) {
			result[category] = filtered
		}
	}
	return result
})

// Count of KNOWN permissions enabled (only counting ones in our defined categories)
const selectedGroupKnownPermCount = computed(() => {
	const perms = activePermsSet.value
	if (perms.size === 0) return 0
	let count = 0
	for (const p of perms) {
		if (allPermissionIds.has(p)) count++
	}
	return count
})

// Total available permissions (from our defined categories)
const totalPermissions = computed(() => {
	return Object.values(permissionCategories).reduce((sum, perms) => sum + perms.length, 0)
})

// Permissions the group has that aren't in our defined list
const unknownPermissions = computed(() => {
	const perms = activePermsSet.value
	if (perms.size === 0) return []
	return [...perms].filter(p => !allPermissionIds.has(p)).sort()
})

watch(() => vhostId?.value, (newVal) => {
	if (newVal) fetchPermissionGroups()
}, { immediate: true })
</script>

<template>
	<div class="p-4 space-y-4">
		<Tabs v-model="activeTab" class="space-y-4">
			<TabsList>
				<TabsTrigger value="groups" class="inline-flex items-center gap-1.5">
					<Shield class="h-4 w-4 shrink-0" /> <span>Permission Groups</span>
				</TabsTrigger>
				<TabsTrigger value="matrix" class="inline-flex items-center gap-1.5">
					<Users class="h-4 w-4 shrink-0" /> <span>Permission Matrix</span>
				</TabsTrigger>
			</TabsList>

			<TabsContent value="groups" class="space-y-4">
				<div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
					<!-- Group List Sidebar -->
					<Card class="lg:col-span-1">
						<CardHeader class="p-3 border-b">
							<div class="flex justify-between items-center">
								<CardTitle class="text-base font-normal">Groups</CardTitle>
								<Dialog v-model:open="showCreateDialog">
									<DialogTrigger as-child>
										<Button size="sm" variant="outline">
											<Plus class="h-4 w-4" />
										</Button>
									</DialogTrigger>
									<DialogContent>
										<DialogHeader>
											<DialogTitle>Create Permission Group</DialogTitle>
											<DialogDescription>Create a new permission group to control what users in this group can access.</DialogDescription>
										</DialogHeader>
										<div class="space-y-4 py-4">
											<div class="space-y-2">
												<Label>Group Name</Label>
												<Input v-model="newGroupName" placeholder="e.g., Editor, Viewer, Moderator" />
											</div>
										</div>
										<DialogFooter>
											<Button @click="createPermissionGroup" :disabled="!newGroupName">Create</Button>
										</DialogFooter>
									</DialogContent>
								</Dialog>
							</div>
						</CardHeader>
						<CardContent class="p-0">
							<div v-if="loading" class="p-4 text-center text-muted-foreground">Loading...</div>
							<div v-else-if="permissionGroups.length === 0" class="p-4 text-center text-muted-foreground text-sm">
								No permission groups found.
							</div>
							<div v-else>
								<div
									v-for="group in permissionGroups"
									:key="group.id"
									class="flex items-center justify-between p-3 border-b cursor-pointer hover:bg-muted/50 transition-colors"
									:class="{ 'bg-muted': selectedGroup?.id === group.id }"
									@click="selectedGroup = group"
								>
									<div class="min-w-0 flex-1">
										<div class="font-medium text-sm">{{ group.name }}</div>
										<div class="text-xs text-muted-foreground">{{ group.permissions?.length ?? 0 }} permissions</div>
									</div>
									<!-- Delete with confirmation -->
									<AlertDialog>
										<AlertDialogTrigger as-child>
											<Button
												variant="ghost"
												size="sm"
												class="h-6 w-6 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 shrink-0 ml-2"
												@click.stop
											>
												<Trash2 class="h-3 w-3" />
											</Button>
										</AlertDialogTrigger>
										<AlertDialogContent>
											<AlertDialogHeader>
												<AlertDialogTitle>Delete "{{ group.name }}"?</AlertDialogTitle>
												<AlertDialogDescription>
													This will permanently remove this permission group. Users currently in this group will lose these permissions. This cannot be undone.
												</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel>Cancel</AlertDialogCancel>
												<AlertDialogAction class="bg-destructive text-destructive-foreground hover:bg-destructive/90" @click="deletePermissionGroup(group.id)">
													Delete Group
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								</div>
							</div>
						</CardContent>
					</Card>

					<!-- Permission Editor -->
					<Card class="lg:col-span-3">
						<CardHeader class="p-3 border-b">
							<div class="flex justify-between items-center">
								<div v-if="selectedGroup">
									<CardTitle class="text-base font-normal">{{ selectedGroup.name }}</CardTitle>
									<p class="text-xs text-muted-foreground mt-1">
										{{ selectedGroupKnownPermCount }} of {{ totalPermissions }} permissions enabled
										<span v-if="unknownPermissions.length > 0" class="text-amber-500">
											(+ {{ unknownPermissions.length }} legacy)
										</span>
									</p>
								</div>
								<CardTitle v-else class="text-base font-normal text-muted-foreground">Select a permission group to edit</CardTitle>
								<div class="flex space-x-2">
									<div class="relative">
										<Search class="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
										<Input v-model="searchQuery" placeholder="Search permissions..." class="pl-8 h-9 w-64" />
									</div>
									<Button v-if="selectedGroup" size="sm" @click="savePermissionGroup">
										<Save class="h-4 w-4 mr-1" /> Save
									</Button>
								</div>
							</div>
						</CardHeader>
						<CardContent class="p-0">
							<div v-if="!selectedGroup" class="p-8 text-center text-muted-foreground">
								Select a permission group from the sidebar to manage its permissions.
							</div>
							<div v-else class="max-h-[600px] overflow-y-auto">
								<div v-for="(perms, category) in filteredCategories" :key="category" class="border-b last:border-b-0">
									<div
										class="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30"
										@click="toggleCategory(category as string)"
									>
										<div class="flex items-center space-x-2">
											<component :is="expandedCategories.has(category as string) ? ChevronDown : ChevronRight" class="h-4 w-4" />
											<span class="font-medium text-sm">{{ category }}</span>
											<Badge variant="secondary" class="text-xs">{{ perms.length }}</Badge>
										</div>
										<div class="text-xs text-muted-foreground">
											{{ perms.filter(p => hasPermission(p.id)).length }}/{{ perms.length }} enabled
										</div>
									</div>
									<div v-if="expandedCategories.has(category as string)" class="pl-6 pb-2">
										<div
											v-for="perm in perms"
											:key="perm.id"
											class="flex items-center justify-between py-2 px-3 hover:bg-muted/20 rounded"
										>
											<div class="flex items-center space-x-3">
												<Checkbox
													:key="`perm-${selectedGroup?.id}-${perm.id}`"
													:model-value="hasPermission(perm.id)"
													@update:model-value="togglePermission(perm.id)"
												/>
												<div>
													<div class="text-sm">{{ perm.name }}</div>
													<div class="text-xs text-muted-foreground">{{ perm.description }}</div>
												</div>
											</div>
											<Badge variant="outline" class="text-xs font-mono shrink-0 ml-2 hidden lg:inline-flex">{{ perm.id }}</Badge>
										</div>
									</div>
								</div>

								<!-- Unknown / Legacy permissions -->
								<div v-if="unknownPermissions.length > 0" class="border-b last:border-b-0">
									<div
										class="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30"
										@click="toggleCategory('__legacy__')"
									>
										<div class="flex items-center space-x-2">
											<component :is="expandedCategories.has('__legacy__') ? ChevronDown : ChevronRight" class="h-4 w-4" />
											<span class="font-medium text-sm text-amber-600">Legacy / Unrecognized</span>
											<Badge variant="secondary" class="text-xs bg-amber-100 text-amber-700">{{ unknownPermissions.length }}</Badge>
										</div>
										<div class="text-xs text-amber-500">
											{{ unknownPermissions.length }} enabled
										</div>
									</div>
									<div v-if="expandedCategories.has('__legacy__')" class="pl-6 pb-2">
										<p class="text-xs text-muted-foreground px-3 pb-2">These permissions exist in the database but aren't in the current permission catalog. They may be from older features or custom integrations.</p>
										<div
											v-for="permId in unknownPermissions"
											:key="permId"
											class="flex items-center justify-between py-2 px-3 hover:bg-muted/20 rounded"
										>
											<div class="flex items-center space-x-3">
												<Checkbox
													:model-value="true"
													@update:model-value="() => {
														const idx = selectedGroup!.permissions.indexOf(permId)
														if (idx >= 0) selectedGroup!.permissions.splice(idx, 1)
													}"
												/>
												<div>
													<div class="text-sm font-mono">{{ permId }}</div>
													<div class="text-xs text-muted-foreground">Legacy permission — uncheck to remove</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</TabsContent>

			<TabsContent value="matrix" class="space-y-4">
				<Card>
					<CardHeader class="p-3 border-b">
						<CardTitle class="text-base font-normal">Permission Matrix</CardTitle>
						<p class="text-xs text-muted-foreground">Overview of permissions across all groups.</p>
					</CardHeader>
					<CardContent class="p-0 overflow-x-auto">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead class="w-[250px] sticky left-0 bg-background">Permission</TableHead>
									<TableHead v-for="group in permissionGroups" :key="group.id" class="text-center min-w-[100px]">
										{{ group.name }}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<template v-for="(perms, category) in permissionCategories" :key="category">
									<TableRow class="bg-muted/30">
										<TableCell :colspan="permissionGroups.length + 1" class="font-medium text-sm py-2">
											{{ category }}
										</TableCell>
									</TableRow>
									<TableRow v-for="perm in perms" :key="perm.id">
										<TableCell class="text-sm sticky left-0 bg-background">
											{{ perm.name }}
										</TableCell>
										<TableCell v-for="group in permissionGroups" :key="group.id" class="text-center">
											<Checkbox
												:key="`matrix-${group.id}-${perm.id}`"
												:model-value="group.permissions?.includes(perm.id.toLowerCase()) ?? false"
												@update:model-value="() => {
													if (!group.permissions) group.permissions = []
													const lc = perm.id.toLowerCase()
													const idx = group.permissions.indexOf(lc)
													if (idx >= 0) group.permissions.splice(idx, 1)
													else group.permissions.push(lc)
												}"
											/>
										</TableCell>
									</TableRow>
								</template>
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</TabsContent>
		</Tabs>
	</div>
</template>
